/**
 * pdp-v2-sticky-atc.js — Sticky Mobile ATC Web Component
 * =========================================================
 * Shows a persistent bottom bar on mobile when the main ATC button
 * scrolls out of the viewport. Hidden when the main ATC is visible.
 *
 * Uses IntersectionObserver on the buy-buttons block container
 * (marked with [data-pdp-v2-atc-target]) for accurate visibility detection.
 *
 * When visible:
 *   - Adds padding-bottom to <body> equal to bar height so content
 *     is not obscured.
 *   - Clicking the bar's ATC button triggers a click on the main
 *     product form's submit button (delegates to theme's cart logic).
 *   - Once tapped, the bar is permanently dismissed for the rest of the
 *     pageview.
 *
 * Drawer awareness:
 *   - theme.Drawers adds "js-drawer-open" to <html> for every drawer
 *     (cart, search, etc.) and removes it on close. This bar force-hides
 *     itself whenever that class is present, independent of dismissal or
 *     scroll state, so it can never sit on top of an open drawer's
 *     checkout button — the bar's z-index otherwise sits above the
 *     drawer's, so simply being visible when a drawer opens (not only
 *     after being tapped) was enough to cover it.
 *
 * Usage (Liquid):
 *   <pdp-v2-sticky-atc data-form-id="AddToCartForm-{section.id}-{product.id}">
 *     ...bar markup rendered by pdp-v2-sticky-atc.liquid...
 *   </pdp-v2-sticky-atc>
 */

class PdpV2StickyAtc extends HTMLElement {
  connectedCallback() {
    // Only run on mobile — IntersectionObserver still fires on desktop but
    // the element is hidden via CSS, so this is just a safety belt.
    this._dismissed = false;
    this._targetVisible = false;
    this._targetAboveViewport = false;
    this._drawerOpen = document.documentElement.classList.contains('js-drawer-open');
    this._setupObserver();
    this._setupDrawerObserver();
    this._setupButton();
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
    }
    if (this._drawerObserver) {
      this._drawerObserver.disconnect();
    }
    document.body.style.removeProperty('padding-bottom');
  }

  _setupObserver() {
    // Find the target element (buy_buttons block) marked in the section Liquid.
    // Walk up to the nearest product section, then look for the target.
    const section = document.querySelector('[data-section-type="product"]');
    const target = section && section.querySelector('[data-pdp-v2-atc-target]');

    if (!target) return;

    this._observer = new IntersectionObserver(
      ([entry]) => {
        if (this._dismissed) return;

        this._targetVisible = entry.isIntersecting;
        // Only show if the target has scrolled above the viewport (not below)
        this._targetAboveViewport = entry.boundingClientRect.top < 0;
        this._updateVisibility();
      },
      { threshold: 0 }
    );

    this._observer.observe(target);
  }

  _setupDrawerObserver() {
    this._drawerObserver = new MutationObserver(() => {
      const isOpen = document.documentElement.classList.contains('js-drawer-open');
      if (isOpen === this._drawerOpen) return;
      this._drawerOpen = isOpen;
      this._updateVisibility();
    });

    this._drawerObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  _updateVisibility() {
    if (this._dismissed || this._drawerOpen) {
      this.hide();
      return;
    }

    if (!this._targetVisible && this._targetAboveViewport) {
      this.show();
    } else {
      this.hide();
    }
  }

  _setupButton() {
    const btn = this.querySelector('[data-sticky-atc-btn]');
    if (!btn) return;

    btn.addEventListener('click', () => {
      // Find the main form's submit button and click it.
      // This delegates to the theme's existing form submission handler
      // (variant selection, quantity, cart drawer open, etc.)
      const formId = this.dataset.formId;
      const mainForm = formId ? document.getElementById(formId) : null;
      const mainSubmitBtn = mainForm
        ? mainForm.querySelector('[name="add"], [type="submit"]')
        : null;

      if (mainSubmitBtn) {
        mainSubmitBtn.click();
      }

      // Permanently dismiss the bar once it's done its job.
      this._dismissed = true;
      if (this._observer) {
        this._observer.disconnect();
      }
      if (this._drawerObserver) {
        this._drawerObserver.disconnect();
      }
      this.hide();
    });
  }

  show() {
    this.removeAttribute('hidden');
    document.body.style.paddingBottom = `${this.offsetHeight}px`;
  }

  hide() {
    this.setAttribute('hidden', '');
    document.body.style.removeProperty('padding-bottom');
  }
}

if (!customElements.get('pdp-v2-sticky-atc')) {
  customElements.define('pdp-v2-sticky-atc', PdpV2StickyAtc);
}
